import { addDays, eachDayOfInterval, getDay, set, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { CalendarWorkingHour } from './calendar-working-hour.model';

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MINUTE_IN_MS = 60 * 1000;

export interface CalendarWorkingHoursInterval {
  start: Date;
  end: Date;
  localDate: Date;
  day: number;
  slot: CalendarWorkingHour;
}

export interface CalendarWorkingHoursRangeOptions {
  workingHours: readonly CalendarWorkingHour[];
  timeZone: string;
  rangeStart: Date;
  rangeEnd: Date;
}

export interface CalendarWorkingHoursStatusOptions {
  workingHours: readonly CalendarWorkingHour[];
  timeZone: string;
  at: Date;
  searchDays?: number;
}

export interface CalendarWorkingHoursStatus {
  isOpen: boolean;
  minutesUntilNextOpen: number | null;
}

export function getCalendarWorkingHoursIntervalsForRange(
  options: CalendarWorkingHoursRangeOptions
): CalendarWorkingHoursInterval[] {
  const { workingHours, timeZone, rangeStart, rangeEnd } = options;
  if (
    workingHours.length === 0 ||
    !timeZone ||
    !isValidDate(rangeStart) ||
    !isValidDate(rangeEnd) ||
    rangeStart >= rangeEnd
  ) {
    return [];
  }

  const localDates = getResourceLocalDatesForRange(rangeStart, rangeEnd, timeZone);
  if (localDates.length === 0) {
    return [];
  }

  const intervals: CalendarWorkingHoursInterval[] = [];
  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();

  for (const localDate of localDates) {
    const day = getCalendarWorkingHoursDayIndex(localDate);
    const daySlots = workingHours.filter((slot) => slot.day === day);

    for (const slot of daySlots) {
      const interval = buildCalendarWorkingHoursInterval(slot, localDate, timeZone);
      if (!interval) {
        continue;
      }

      const intervalStartMs = interval.start.getTime();
      const intervalEndMs = interval.end.getTime();
      if (intervalEndMs <= rangeStartMs || intervalStartMs >= rangeEndMs) {
        continue;
      }

      intervals.push(interval);
    }
  }

  return intervals.sort((left, right) => left.start.getTime() - right.start.getTime());
}

export function isDateRangeWithinCalendarWorkingHours(options: CalendarWorkingHoursRangeOptions): boolean {
  const { rangeStart, rangeEnd } = options;
  if (!isValidDate(rangeStart) || !isValidDate(rangeEnd) || rangeStart >= rangeEnd) {
    return false;
  }

  const intervals = getCalendarWorkingHoursIntervalsForRange(options);
  if (intervals.length === 0) {
    return false;
  }

  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();
  let coveredUntilMs = rangeStartMs;

  for (const interval of intervals) {
    const intervalStartMs = Math.max(interval.start.getTime(), rangeStartMs);
    const intervalEndMs = Math.min(interval.end.getTime(), rangeEndMs);

    if (intervalEndMs <= coveredUntilMs) {
      continue;
    }

    if (intervalStartMs > coveredUntilMs) {
      return false;
    }

    coveredUntilMs = Math.max(coveredUntilMs, intervalEndMs);
    if (coveredUntilMs >= rangeEndMs) {
      return true;
    }
  }

  return coveredUntilMs >= rangeEndMs;
}

export function getCalendarWorkingHoursStatusAt(
  options: CalendarWorkingHoursStatusOptions
): CalendarWorkingHoursStatus {
  const { at, searchDays = 8 } = options;
  if (!isValidDate(at)) {
    return { isOpen: false, minutesUntilNextOpen: null };
  }

  const atMs = at.getTime();
  const intervals = getCalendarWorkingHoursIntervalsForRange({
    workingHours: options.workingHours,
    timeZone: options.timeZone,
    rangeStart: at,
    rangeEnd: new Date(atMs + Math.max(searchDays, 1) * DAY_IN_MS),
  });

  for (const interval of intervals) {
    const startMs = interval.start.getTime();
    const endMs = interval.end.getTime();

    if (startMs <= atMs && atMs < endMs) {
      return { isOpen: true, minutesUntilNextOpen: 0 };
    }

    if (startMs > atMs) {
      return {
        isOpen: false,
        minutesUntilNextOpen: Math.ceil((startMs - atMs) / MINUTE_IN_MS),
      };
    }
  }

  return { isOpen: false, minutesUntilNextOpen: null };
}

function buildCalendarWorkingHoursInterval(
  slot: CalendarWorkingHour,
  localDate: Date,
  timeZone: string
): CalendarWorkingHoursInterval | null {
  const startMinutes = parseWorkingHoursPeriodToMinutes(slot.startPeriod);
  const endMinutes = parseWorkingHoursPeriodToMinutes(slot.endPeriod);
  if (startMinutes === null || endMinutes === null || startMinutes >= 24 * 60 || endMinutes > 24 * 60) {
    return null;
  }

  const endOnNextDate = endMinutes === 24 * 60 || endMinutes <= startMinutes;
  const localStart = setLocalMinutes(localDate, startMinutes);
  const localEnd = setLocalMinutes(endOnNextDate ? addDays(localDate, 1) : localDate, endMinutes);

  let start: Date;
  let end: Date;
  try {
    start = fromZonedTime(localStart, timeZone);
    end = fromZonedTime(localEnd, timeZone);
  } catch {
    return null;
  }

  if (!isValidDate(start) || !isValidDate(end) || end <= start) {
    return null;
  }

  return { start, end, localDate, day: slot.day, slot };
}

function getResourceLocalDatesForRange(rangeStart: Date, rangeEnd: Date, timeZone: string): Date[] {
  try {
    const firstLocalDate = startOfDay(toZonedTime(new Date(rangeStart.getTime() - DAY_IN_MS), timeZone));
    const lastLocalDate = startOfDay(toZonedTime(new Date(rangeEnd.getTime() + DAY_IN_MS), timeZone));

    return eachDayOfInterval({ start: firstLocalDate, end: lastLocalDate });
  } catch {
    return [];
  }
}

function getCalendarWorkingHoursDayIndex(localDate: Date): number {
  return (getDay(localDate) + 6) % 7;
}

function setLocalMinutes(localDate: Date, totalMinutes: number): Date {
  const normalizedMinutes = totalMinutes % (24 * 60);

  return set(localDate, {
    hours: Math.floor(normalizedMinutes / 60),
    minutes: normalizedMinutes % 60,
    seconds: 0,
    milliseconds: 0,
  });
}

function parseWorkingHoursPeriodToMinutes(period: string): number | null {
  const [rawHours = '', rawMinutes = ''] = period.split(':');
  const hours = Number.parseInt(rawHours, 10);
  const minutes = Number.parseInt(rawMinutes, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function isValidDate(value: Date): boolean {
  return value instanceof Date && !Number.isNaN(value.getTime());
}
